import type {
  AuthUser,
  Instance,
  InstanceWithUser,
  InstanceEvent,
  InstanceStatusResponse,
  SubdomainCheck,
  CloudUser,
  ResourcePlan,
  ResourcePlanSummary,
  Setting,
  ProviderImage,
  ProviderSshKey,
  ConnectedProvidersResponse,
  GitRepoPageResponse,
  ImportRepoParams,
  ImportResult,
  UpdateStatusResponse,
  PublishVersionResponse,
  TriggerUpdateResponse,
  SelfHostedInstance,
  AdminSelfHostedInstance,
  RegisterSelfHostedRequest,
  UpdateSelfHostedRequest,
  SelfHostedHealthResponse,
  TunnelConfigResponse,
} from "../../types/cloud";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("cloud_access_token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// ─── Token Refresh ─────────────────────────────────────────────────────

let refreshPromise: Promise<{ accessToken: string } | null> | null = null;

export async function refreshToken(): Promise<{ accessToken: string } | null> {
  try {
    const response = await fetch("/api/auth/keycloak/refresh", {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) {
      localStorage.removeItem("cloud_access_token");
      return null;
    }

    const data: { accessToken: string } = await response.json();
    localStorage.setItem("cloud_access_token", data.accessToken);
    return data;
  } catch {
    localStorage.removeItem("cloud_access_token");
    return null;
  }
}

/**
 * Attempt a token refresh, deduplicating concurrent calls so only one
 * network request is made at a time (prevents thundering herd).
 */
async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) {
    const result = await refreshPromise;
    return result !== null;
  }

  refreshPromise = refreshToken();
  try {
    const result = await refreshPromise;
    return result !== null;
  } finally {
    refreshPromise = null;
  }
}

// ─── API Request with 401 Retry ────────────────────────────────────────

async function apiRequest<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  });

  // On 401, attempt token refresh and retry the request once
  if (res.status === 401) {
    const refreshed = await attemptRefresh();
    if (refreshed) {
      const retryRes = await fetch(url, {
        ...options,
        headers: {
          ...getAuthHeaders(),
          ...options?.headers,
        },
      });

      if (!retryRes.ok) {
        const body = await retryRes.json().catch(() => ({ message: retryRes.statusText }));
        const message = typeof body.message === "string" ? body.message : retryRes.statusText;
        throw new ApiError(retryRes.status, message);
      }

      if (retryRes.status === 204) {
        return undefined as T;
      }

      return retryRes.json() as Promise<T>;
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    const message = typeof body.message === "string" ? body.message : res.statusText;
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ─── Auth ──────────────────────────────────────────────────────────────

export function getLoginUrl(redirect?: string): Promise<{ url: string }> {
  const params = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";
  return apiRequest(`/api/auth/keycloak/login-url${params}`);
}

export function handleAuthCallback(
  code: string,
  redirectUri: string
): Promise<{ user: AuthUser; accessToken: string }> {
  return apiRequest("/api/auth/keycloak/callback", {
    method: "POST",
    body: JSON.stringify({ code, redirectUri }),
  });
}

export function getMe(): Promise<AuthUser> {
  return apiRequest("/api/auth/keycloak/me");
}

export async function logout(): Promise<{ logoutUrl: string } | null> {
  try {
    const response = await fetch("/api/auth/keycloak/logout", {
      method: "POST",
      credentials: "include",
    });

    if (!response.ok) return null;

    return response.json();
  } catch {
    return null;
  }
}

// ─── User Plans ────────────────────────────────────────────────────────

export function listAvailablePlans(): Promise<{ plans: ResourcePlanSummary[] }> {
  return apiRequest("/api/plans");
}

export function updateMyPlan(planId: string): Promise<AuthUser> {
  return apiRequest("/api/users/me/plan", {
    method: "PATCH",
    body: JSON.stringify({ planId }),
  });
}

// ─── User Instances ────────────────────────────────────────────────────

export function getMyInstance(): Promise<Instance> {
  return apiRequest("/api/instances/mine");
}

export function checkSubdomain(name: string): Promise<SubdomainCheck> {
  return apiRequest(`/api/instances/check-subdomain?name=${encodeURIComponent(name)}`);
}

export function createInstance(subdomain: string): Promise<Instance> {
  return apiRequest("/api/instances", {
    method: "POST",
    body: JSON.stringify({ subdomain }),
  });
}

export function startInstance(id: string): Promise<Instance> {
  return apiRequest(`/api/instances/${id}/start`, { method: "POST" });
}

export function stopInstance(id: string): Promise<Instance> {
  return apiRequest(`/api/instances/${id}/stop`, { method: "POST" });
}

export function deleteInstance(id: string): Promise<void> {
  return apiRequest(`/api/instances/${id}`, { method: "DELETE" });
}

export function getInstanceStatus(id: string): Promise<InstanceStatusResponse> {
  return apiRequest(`/api/instances/${id}/status`);
}

export function getInstanceEvents(id: string): Promise<InstanceEvent[]> {
  return apiRequest(`/api/instances/${id}/events`);
}

// ─── Admin Users ───────────────────────────────────────────────────────

export function listUsers(): Promise<{ users: CloudUser[]; total: number }> {
  return apiRequest("/api/admin/users");
}

export function getUser(id: string): Promise<CloudUser> {
  return apiRequest(`/api/admin/users/${id}`);
}

export function updateUser(
  id: string,
  data: { status?: string; role?: string; planId?: string }
): Promise<CloudUser> {
  return apiRequest(`/api/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ─── Admin Instances ───────────────────────────────────────────────────

export function listAllInstances(
  status?: string
): Promise<{ instances: InstanceWithUser[]; total: number }> {
  const params = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiRequest(`/api/admin/instances${params}`);
}

export function getInstanceById(id: string): Promise<InstanceWithUser> {
  return apiRequest(`/api/admin/instances/${id}`);
}

export function adminForceStop(id: string): Promise<Instance> {
  return apiRequest(`/api/admin/instances/${id}/stop`, { method: "POST" });
}

export function adminForceStart(id: string): Promise<Instance> {
  return apiRequest(`/api/admin/instances/${id}/start`, { method: "POST" });
}

export function adminForceDelete(id: string): Promise<void> {
  return apiRequest(`/api/admin/instances/${id}`, { method: "DELETE" });
}

export function adminUpdateInstance(
  id: string,
  data: { status?: string; errorMessage?: string }
): Promise<Instance> {
  return apiRequest(`/api/admin/instances/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ─── Admin Plans ───────────────────────────────────────────────────────

export function listPlans(): Promise<{ plans: ResourcePlan[]; total: number }> {
  return apiRequest("/api/admin/plans");
}

export function createPlan(data: {
  name: string;
  cpuCores: number;
  ramMb: number;
  diskGb: number;
  maxProjects: number;
  providerPresetId?: string;
  active?: boolean;
}): Promise<ResourcePlan> {
  return apiRequest("/api/admin/plans", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updatePlan(
  id: string,
  data: Partial<{
    name: string;
    cpuCores: number;
    ramMb: number;
    diskGb: number;
    maxProjects: number;
    providerPresetId: string;
    active: boolean;
  }>
): Promise<ResourcePlan> {
  return apiRequest(`/api/admin/plans/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deletePlan(id: string): Promise<void> {
  return apiRequest(`/api/admin/plans/${id}`, { method: "DELETE" });
}

// ─── Admin Provider ────────────────────────────────────────────────────

export function listProviderImages(): Promise<ProviderImage[]> {
  return apiRequest("/api/admin/provider/images");
}

export function listProviderSshKeys(): Promise<ProviderSshKey[]> {
  return apiRequest("/api/admin/provider/ssh-keys");
}

// ─── Admin Settings ────────────────────────────────────────────────────

export function getSettings(): Promise<{ settings: Setting[] }> {
  return apiRequest("/api/admin/settings");
}

export function updateSettings(updates: Record<string, unknown>): Promise<{ settings: Setting[] }> {
  return apiRequest("/api/admin/settings", {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// ─── Git Providers ─────────────────────────────────────────────────────

export function getConnectedProviders(): Promise<ConnectedProvidersResponse> {
  return apiRequest("/api/git-providers");
}

export function listRepos(
  provider: string,
  options?: { page?: number; search?: string }
): Promise<GitRepoPageResponse> {
  const params = new URLSearchParams();
  if (options?.page) params.set("page", String(options.page));
  if (options?.search) params.set("search", options.search);
  const qs = params.toString();
  return apiRequest(
    `/api/git-providers/${encodeURIComponent(provider)}/repos${qs ? `?${qs}` : ""}`
  );
}

export function importRepo(params: ImportRepoParams): Promise<ImportResult> {
  return apiRequest("/api/git-providers/import", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// ─── Admin Updates ─────────────────────────────────────────────────────

export function publishVersion(
  version: string,
  gitRef?: string,
  description?: string
): Promise<PublishVersionResponse> {
  return apiRequest("/api/admin/updates/publish", {
    method: "POST",
    body: JSON.stringify({ version, gitRef, description }),
  });
}

export function getUpdateStatus(): Promise<UpdateStatusResponse> {
  return apiRequest("/api/admin/updates/status");
}

export function triggerInstanceUpdate(instanceId: string): Promise<TriggerUpdateResponse> {
  return apiRequest(`/api/admin/updates/instances/${instanceId}/trigger`, {
    method: "POST",
  });
}

// ─── Self-Hosted Instances ────────────────────────────────────────────────

export function getSelfHostedInstances(): Promise<SelfHostedInstance[]> {
  return apiRequest("/api/self-hosted/instances");
}

export function registerSelfHostedInstance(data: RegisterSelfHostedRequest): Promise<SelfHostedInstance> {
  return apiRequest("/api/self-hosted/instances", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSelfHostedInstance(id: string, data: UpdateSelfHostedRequest): Promise<SelfHostedInstance> {
  return apiRequest(`/api/self-hosted/instances/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteSelfHostedInstance(id: string): Promise<void> {
  return apiRequest(`/api/self-hosted/instances/${id}`, { method: "DELETE" });
}

export function checkSelfHostedHealth(id: string): Promise<SelfHostedHealthResponse> {
  return apiRequest(`/api/self-hosted/instances/${id}/check-health`, { method: "POST" });
}

export function getTunnelConfig(id: string): Promise<TunnelConfigResponse> {
  return apiRequest(`/api/self-hosted/instances/${id}/tunnel-config`);
}

// ─── Admin Self-Hosted Instances ──────────────────────────────────────────

export function adminListSelfHostedInstances(options?: {
  status?: string;
  mode?: string;
}): Promise<{ instances: AdminSelfHostedInstance[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.status) params.set("status", options.status);
  if (options?.mode) params.set("mode", options.mode);
  const qs = params.toString();
  return apiRequest(`/api/admin/self-hosted/instances${qs ? `?${qs}` : ""}`);
}

export function adminGetSelfHostedInstance(id: string): Promise<AdminSelfHostedInstance> {
  return apiRequest(`/api/admin/self-hosted/instances/${id}`);
}

export function adminUpdateSelfHostedInstance(
  id: string,
  data: { status?: string; errorMessage?: string },
): Promise<AdminSelfHostedInstance> {
  return apiRequest(`/api/admin/self-hosted/instances/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function adminDeleteSelfHostedInstance(id: string): Promise<void> {
  return apiRequest(`/api/admin/self-hosted/instances/${id}`, { method: "DELETE" });
}
